import { useState } from "react";
import { usePolling } from "../lib/use-polling";
import { api, type ManagedQueue } from "../lib/api";
import { PageHelp } from "../components/PageHelp";
import { Table, type Column } from "../components/primitives/Table";
import { Button } from "../components/primitives/Button";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";
import { InfoTip } from "../components/primitives/InfoTip";
import { FormField, TextInput } from "../components/primitives/FormField";
import { Modal } from "../components/primitives/Modal";
import { PageHeader } from "../components/primitives/PageHeader";
import { useConfirm } from "../components/primitives/Confirm";

const AWS_ACCOUNT_RE = /^\d{12}$/;

// One form modal replaces the two prompts (spec §6.5). Validation happens in
// the modal; the row's busy state is owned by the page.
/**
 * Change which AWS account and principal may consume a queue.
 *
 * Tier 1: this rewrites a live queue policy, so the dialog names the queue and
 * stays put while the request is in flight rather than letting Escape or a
 * backdrop click imply the change was called off.
 */
export function EditAccessModal({
  queue,
  onClose,
  onSaved,
}: {
  queue: ManagedQueue;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [accountId, setAccountId] = useState(queue.consumerAwsAccountId);
  const [principalArn, setPrincipalArn] = useState(queue.consumerPrincipalArn ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Save who may consume this queue, after checking the account id looks like one. */
  async function save() {
    if (!AWS_ACCOUNT_RE.test(accountId.trim())) {
      setError("Consumer AWS account id must be a 12-digit number.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.updateQueueAccess(queue.id, {
        consumerAwsAccountId: accountId.trim(),
        consumerPrincipalArn: principalArn.trim() || undefined,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Edit access"
      onClose={onClose}
      dismissible={!busy}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save access"}
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate>
        <p className="page-help" style={{ marginTop: 0 }}>
          Anansi will update the queue policy in AWS. The new consumer can read immediately; the old one loses access.
        </p>
        <FormField label="Consumer AWS account id" hint="The 12-digit AWS account that reads from this queue.">
          <TextInput aria-label="Consumer AWS account id" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
        </FormField>
        <FormField label="Consumer principal ARN" hint="Optional. A specific IAM role or user. Leave blank to allow the whole account.">
          <TextInput aria-label="Consumer principal ARN" value={principalArn} onChange={(e) => setPrincipalArn(e.target.value)} placeholder="arn:aws:iam::123456789012:role/my-role" />
        </FormField>
        {error ? <div className="field__error" role="alert">{error}</div> : null}
      </form>
    </Modal>
  );
}

/** The managed queues, their depth, and who is allowed to read from them. */
export function Queues() {
  const q = usePolling(() => api.listQueues(), 30000);
  const [showNew, setShowNew] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [name, setName] = useState("");
  const [fifo, setFifo] = useState(false);
  const [consumerAwsAccountId, setConsumer] = useState("");
  const [consumerPrincipalArn, setPrincipal] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const [editing, setEditing] = useState<ManagedQueue | null>(null);

  /** The first thing wrong with the new-queue form, or null when it is ready. */
  function validate(): string | null {
    if (!orgId.trim()) return "Client id is required.";
    if (!name.trim()) return "Name is required.";
    if (!AWS_ACCOUNT_RE.test(consumerAwsAccountId.trim()))
      return "Consumer AWS account ID must be a 12-digit number.";
    return null;
  }

  /** Create the queue in AWS and clear the form for the next one. */
  async function onCreate() {
    const v = validate();
    if (v) { setError(v); return; }
    setCreating(true);
    setError(null);
    try {
      await api.createQueue({
        organizationId: orgId.trim(),
        name: name.trim(),
        fifo,
        consumerAwsAccountId: consumerAwsAccountId.trim(),
        consumerPrincipalArn: consumerPrincipalArn.trim() || undefined,
      });
      setShowNew(false);
      setOrgId("");
      setName("");
      setConsumer("");
      setPrincipal("");
      setFifo(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  /** Delete the queue in AWS. Tier 2 — anything still in it is lost and the
   *  client's consumer stops receiving events. */
  async function onRemove(row: ManagedQueue) {
    setError(null);
    await confirm({
      tier: 2,
      title: `Remove ${row.name}?`,
      body: "This deletes the queue in AWS. Messages still in it are lost and the client's consumer stops receiving events. This cannot be undone.",
      confirmLabel: "Remove queue",
      typedPhrase: row.name,
      onConfirm: async () => { await api.deleteQueue(row.id); },
    });
  }

  if (q.status === "loading") return <Pulse />;

  const rows: ManagedQueue[] = (q.data?.queues ?? []) as ManagedQueue[];

  const columns: Column<ManagedQueue>[] = [
    { key: "name", label: "NAME", mono: true, render: (r) => r.name },
    {
      key: "queueUrl",
      label: "QUEUE URL",
      mono: true,
      render: (r) => (
        <span style={{ color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)" }}>
          {r.queueUrl}
        </span>
      ),
    },
    {
      key: "type",
      label: "TYPE",
      render: (r) => (
        <Badge tone={r.fifo ? "ok" : "muted"}>{r.fifo ? "FIFO" : "STANDARD"}</Badge>
      ),
    },
    {
      key: "consumer",
      label: "CONSUMER",
      mono: true,
      render: (r) => (
        <span style={{ fontFamily: "var(--font-mono)" }}>
          {r.consumerPrincipalArn ?? r.consumerAwsAccountId}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <div style={{ display: "inline-flex", gap: 8 }}>
          <Button variant="ghost" onClick={() => setEditing(r)}>
            Edit access
          </Button>
          <Button variant="ghost" onClick={() => void onRemove(r)}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  const canCreate =
    !creating &&
    orgId.trim().length > 0 &&
    name.trim().length > 0 &&
    consumerAwsAccountId.trim().length > 0;

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            Advanced
            <InfoTip termKey="managed-queue" />
          </>
        }
        title="Queues"
        actions={<Button onClick={() => setShowNew((s) => !s)}>{showNew ? "Cancel" : "New queue"}</Button>}
      />
      <PageHelp />

      {error && (
        <div className="field__error" style={{ marginBottom: 12 }}>{error}</div>
      )}

      {showNew && (
        <div style={{ border: "1px solid var(--color-border)", padding: 16, marginBottom: 24, maxWidth: 720 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>NEW QUEUE</div>
          <p className="page-help" style={{ margin: "0 0 12px" }}>
            Anansi provisions an SQS queue in the platform account and grants read access to the
            consumer you specify. Once created, the queue URL can be used as a custom SQS destination
            in any pipeline for this client.
          </p>
          <FormField label="CLIENT ID *" hint="The client this queue belongs to.">
            <TextInput
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder="org-uuid"
            />
          </FormField>
          <FormField label="NAME *" hint="A short, descriptive name for this queue.">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="rip-city-events"
            />
          </FormField>
          <FormField label="CONSUMER AWS ACCOUNT ID *" hint="The 12-digit AWS account ID that will read from this queue.">
            <TextInput
              value={consumerAwsAccountId}
              onChange={(e) => setConsumer(e.target.value)}
              placeholder="123456789012"
            />
          </FormField>
          <FormField label="CONSUMER PRINCIPAL ARN" hint="Optional. A specific IAM role or user ARN. Defaults to the root of the consumer account.">
            <TextInput
              value={consumerPrincipalArn}
              onChange={(e) => setPrincipal(e.target.value)}
              placeholder="arn:aws:iam::123456789012:role/my-role"
            />
          </FormField>
          <FormField label="FIFO" hint="FIFO queues preserve order and deduplicate by message group. Standard queues offer higher throughput.">
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={fifo}
                onChange={(e) => setFifo(e.target.checked)}
              />
              <span className="page-help" style={{ margin: 0 }}>Enable FIFO</span>
            </label>
          </FormField>
          <Button variant="primary" disabled={!canCreate} onClick={onCreate}>
            {creating ? "CREATING…" : "CREATE"}
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        empty={<div className="page-help">No queues yet. Create one to provision an SQS destination for a client.</div>}
      />

      {editing ? (
        <EditAccessModal
          queue={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
